import {type FileDiffStatus, FILE_DIFF_STATUS} from './types/file_tree.ts';

type DiffPageInfo = {pageId: string; name: string; basePageId?: string; status?: FileDiffStatus};
type ConvertPage = {id: string; name: string};
type ConvertHeadJson = {pages?: ConvertPage[]} | null;
export type MergedPage = {pageId: string; name: string; basePageId: string; status: FileDiffStatus};

/**
 * Merges the diff JSON's per-page statuses with the convert JSON's full page
 * list. The convert JSON is the source of truth for rendering order and the
 * universe of pages; the diff JSON contributes per-page statuses and the
 * base-commit page ID (used downstream for cross-commit diffing of renamed
 * pages). Pages present only in the convert JSON get STATUS.UNCHANGED and
 * basePageId defaulted to pageId (unchanged pages cannot be renamed).
 *
 * Falls back to the diff JSON's page list when the convert JSON is unavailable
 * (null or missing pages array) — this preserves the status quo behavior for
 * schematic formats that the convert JSON can't describe (e.g. Altium).
 */
export function mergePages(
  diffHead: DiffPageInfo[] | null,
  convertHead: ConvertHeadJson,
): MergedPage[] {
  const diffPages = diffHead ?? [];

  const diffPageStatus = new Map<string, FileDiffStatus>();
  const diffPageBase = new Map<string, string>();
  for (const page of diffPages) {
    diffPageStatus.set(page.pageId, page.status ?? FILE_DIFF_STATUS.UNCHANGED);
    diffPageBase.set(page.pageId, page.basePageId ?? page.pageId);
  }

  const convertPages = convertHead?.pages;
  if (convertPages && convertPages.length > 0) {
    const convertPageIds = new Set(convertPages.map((p) => p.id));

    const merged: MergedPage[] = convertPages.map((p) => ({
      pageId: p.id,
      name: p.name,
      basePageId: diffPageBase.get(p.id) ?? p.id,
      status: diffPageStatus.get(p.id) ?? FILE_DIFF_STATUS.UNCHANGED,
    }));

    // Append diff-only pages (e.g. deleted sheets) not present in the convert
    // JSON, which represents only the head commit's page universe.
    for (const page of diffPages) {
      if (!convertPageIds.has(page.pageId)) {
        merged.push({
          pageId: page.pageId,
          name: page.name,
          basePageId: page.basePageId ?? page.pageId,
          status: page.status ?? FILE_DIFF_STATUS.UNCHANGED,
        });
      }
    }

    return merged;
  }

  return diffPages.map((p) => ({
    pageId: p.pageId,
    name: p.name,
    basePageId: p.basePageId ?? p.pageId,
    status: p.status ?? FILE_DIFF_STATUS.UNCHANGED,
  }));
}
